// src/components/issues/tableau/MenuColonnes.tsx — SCE Issues
// Le menu « Colonnes » : cocher, réordonner (poignée ou flèches), revenir aux
// colonnes d'origine, ajuster les largeurs. Les flèches existent parce que le
// glisser-déposer n'est pas toujours possible (clavier, tactile).

import { useState } from 'react'
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'
import {
  DEFINITIONS_COLONNES,
  basculerColonne,
  colonnesDOrigine,
  colonnesProposees,
  deplacerColonne,
  placerAvant,
  type ColonneId,
  type ReglagesTableau,
} from '@/utils/tableauProblemes'

export interface MenuColonnesProps {
  ancre: HTMLElement
  reglages: ReglagesTableau
  interne: boolean
  modifier: (changer: (prev: ReglagesTableau) => ReglagesTableau) => void
  onClose: () => void
}

export function MenuColonnes({ ancre, reglages, interne, modifier, onClose }: MenuColonnesProps): JSX.Element {
  const [glissee, setGlissee] = useState<ColonneId | null>(null)
  const [survolee, setSurvolee] = useState<ColonneId | null>(null)
  const proposees = colonnesProposees(interne)
  // L'ordre mémorisé contient toutes les colonnes ; on montre celles de la personne.
  const ordre = reglages.ordre.filter((c) => proposees.includes(c))

  return (
    <Popover ancre={ancre} onClose={onClose} titre="Colonnes affichées" largeur={320} alignerADroite>
      <div className="popover-entete">
        <span>Colonnes affichées</span>
        <button type="button" onClick={() => modifier(colonnesDOrigine)}>
          Colonnes d’origine
        </button>
      </div>
      <label className="menu-colonnes-option">
        <input
          type="checkbox"
          checked={reglages.extrait}
          onChange={(e) => modifier((r) => ({ ...r, extrait: e.target.checked }))}
        />
        <span>Extrait de la description à la suite du titre</span>
      </label>
      <ul className="menu-colonnes">
        {ordre.map((col, n) => {
          const def = DEFINITIONS_COLONNES[col]
          const visible = reglages.visibles.includes(col)
          return (
            <li
              key={col}
              draggable
              className={
                'menu-colonnes-ligne' +
                (glissee === col ? ' menu-colonnes-ligne--glissee' : '') +
                (survolee === col && glissee && glissee !== col ? ' menu-colonnes-ligne--cible' : '')
              }
              onDragStart={(e) => {
                setGlissee(col)
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', col)
              }}
              onDragOver={(e) => {
                if (!glissee) return
                e.preventDefault()
                setSurvolee(col)
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (glissee) modifier((r) => ({ ...r, ordre: placerAvant(r.ordre, glissee, col) }))
                setGlissee(null)
                setSurvolee(null)
              }}
              onDragEnd={() => {
                setGlissee(null)
                setSurvolee(null)
              }}
            >
              <span className="menu-colonnes-poignee" aria-hidden="true" title="Glisser pour déplacer">
                <GripVertical size={16} />
              </span>
              <label className="menu-colonnes-libelle">
                <input
                  type="checkbox"
                  checked={visible}
                  disabled={def.verrouillee}
                  onChange={(e) =>
                    modifier((r) => ({ ...r, visibles: basculerColonne(r.visibles, col, e.target.checked) }))
                  }
                />
                <span>{def.libelle}</span>
                {def.verrouillee && <small>toujours affiché</small>}
              </label>
              <button
                type="button"
                className="menu-colonnes-fleche"
                aria-label={`Monter ${def.libelle}`}
                disabled={n === 0}
                onClick={() => modifier((r) => ({ ...r, ordre: deplacerColonne(r.ordre, col, -1) }))}
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                className="menu-colonnes-fleche"
                aria-label={`Descendre ${def.libelle}`}
                disabled={n === ordre.length - 1}
                onClick={() => modifier((r) => ({ ...r, ordre: deplacerColonne(r.ordre, col, 1) }))}
              >
                <ArrowDown size={14} />
              </button>
            </li>
          )
        })}
      </ul>
      <div className="popover-pied">
        <span>Glissez la poignée ou utilisez les flèches.</span>
        <button
          type="button"
          onClick={() => modifier((r) => ({ ...r, largeurs: {} }))}
          disabled={Object.keys(reglages.largeurs).length === 0}
        >
          Ajuster les largeurs
        </button>
      </div>
    </Popover>
  )
}
